import React, { useState, useEffect } from "react";
import { Card, Table, Tag, Empty, Spin, Typography, Space, Descriptions } from "antd";
import { FileTextOutlined, ClusterOutlined } from "@ant-design/icons";
import axios from "axios";

const { Text } = Typography;

/**
 * Shows batch instructions associated with this job.
 * Displays stage-wise instructions generated as part of a production batch.
 */
function BatchInstructionsPanel({ jobId }) {
  const [loading, setLoading] = useState(false);
  const [instructions, setInstructions] = useState([]);
  const [batchInfo, setBatchInfo] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        // Find work instructions linked via batch for this job
        const res = await axios.get(`production/instruction/job/${jobId}`);
        const all = res.data || [];
        // Filter instructions that have batch_id (batch instructions)
        const batchInstructions = all.filter((i) => i.batch_id);
        setInstructions(batchInstructions);

        // Get batch info if any batch instruction exists
        if (batchInstructions.length > 0 && batchInstructions[0].batch_number) {
          setBatchInfo({
            batch_number: batchInstructions[0].batch_number,
            batch_id: batchInstructions[0].batch_id,
          });
        }
      } catch (_) {}
      setLoading(false);
    };
    fetch();
  }, [jobId]);

  if (loading) return <Spin />;
  if (instructions.length === 0) return null; // Don't render if no batch instructions

  return (
    <Card
      title={
        <Space>
          <ClusterOutlined />
          <span>Batch Instructions</span>
          {batchInfo && <Tag color="purple">{batchInfo.batch_number}</Tag>}
        </Space>
      }
      size="small"
      style={{ marginTop: 16 }}
    >
      <Table
        size="small"
        dataSource={instructions}
        rowKey="id"
        pagination={false}
        columns={[
          {
            title: "Stage",
            dataIndex: "stage_name",
            width: 140,
            render: (v, r) => (
              <Space>
                <FileTextOutlined />
                <Text>{v || r.stage_code}</Text>
              </Space>
            ),
          },
          {
            title: "Type",
            dataIndex: "instruction_type",
            width: 120,
            render: (v) => <Tag>{v}</Tag>,
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 120,
            render: (v) => {
              const colors = {
                GENERATED: "blue",
                PRINTED: "cyan",
                IN_PROGRESS: "orange",
                COMPLETED: "green",
              };
              return <Tag color={colors[v]}>{v}</Tag>;
            },
          },
          {
            title: "Jobs in Batch",
            dataIndex: ["content", "jobs"],
            render: (jobs) =>
              jobs?.length ? (
                <Text type="secondary">
                  {jobs.map((j) => j.job_number).join(", ")}
                </Text>
              ) : (
                "-"
              ),
          },
          {
            title: "Total Qty",
            dataIndex: ["content", "total_quantity"],
            width: 100,
            render: (v) => v?.toLocaleString() || "-",
          },
        ]}
        expandable={{
          expandedRowRender: (record) => {
            const content = record.content || {};
            return (
              <Descriptions size="small" column={3} bordered>
                <Descriptions.Item label="RS">{content.roll_size}"</Descriptions.Item>
                <Descriptions.Item label="CS">{content.cutting_size || "-"}"</Descriptions.Item>
                <Descriptions.Item label="Flute">{content.flute_type || "-"}</Descriptions.Item>
                {content.notes && (
                  <Descriptions.Item label="Notes" span={3}>{content.notes}</Descriptions.Item>
                )}
              </Descriptions>
            );
          },
        }}
      />
    </Card>
  );
}

export default BatchInstructionsPanel;
